import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Sessão do portal — separada da de operação em tudo o que pode ser separado.
 *
 * ## A chave de armazenamento é outra, e isso é o cookie host-only da §c.2
 *
 * O documento pede cookie *host-only* em `suporte.…`, **nunca**
 * `Domain=.aptum.com.br`: um cookie de domínio pai é enviado para os dois
 * hosts, e a separação de audiência morre no primeiro XSS em qualquer
 * subdomínio. A Central não usa cookie de sessão em lado nenhum — o refresh
 * mora no `localStorage`, que é **por origem** pela política do navegador.
 * `suporte.aptum.com.br` e `central.aptum.com.br` são origens distintas: nem
 * com a mesma chave uma leria o que a outra guardou. A chave diferente existe
 * só para o caso de desenvolvimento, em que as duas rodam em `localhost`.
 *
 * O access token continua só em memória e o refresh é rotativo — mesma
 * decisão, mesmas razões que o store de operação.
 */

export interface ContatoDoPortal {
  id: string;
  nome: string;
  email: string;
  empresa: string;
  permissoes: string[];
}

interface RespostaDeAutenticacao {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  mfaRequired: boolean;
  user: { id: string; name: string; email: string };
}

interface EstadoDoPortal {
  accessToken: string | null;
  refreshToken: string | null;
  contato: ContatoDoPortal | null;
  situacao: 'anonimo' | 'entrando' | 'autenticado' | 'mfa-pendente';

  entrar: (email: string, senha: string, tenantSlug?: string) => Promise<void>;
  confirmarMfa: (codigo: string) => Promise<void>;
  renovar: () => Promise<boolean>;
  carregarContato: () => Promise<void>;
  sair: () => Promise<void>;
}

const BASE = '/api/v1/portal';

/**
 * Renovação em andamento, compartilhada.
 *
 * O refresh é rotativo: duas renovações concorrentes com o mesmo token fazem o
 * servidor detectar reuso e revogar a **família inteira** — comportamento
 * correto dele, e desastroso para quem só abriu duas abas.
 */
let renovacaoEmCurso: Promise<boolean> | null = null;

async function autenticar(
  caminho: string,
  corpo: unknown,
  token?: string | null,
): Promise<RespostaDeAutenticacao> {
  const resposta = await fetch(`${BASE}${caminho}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(corpo),
  });
  const texto = await resposta.text();
  const carga = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};
  if (!resposta.ok) {
    throw new Error((carga['message'] as string) ?? 'Não foi possível entrar.');
  }
  return carga as unknown as RespostaDeAutenticacao;
}

export const usePortalAuth = create<EstadoDoPortal>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      contato: null,
      situacao: 'anonimo',

      async entrar(email, senha, tenantSlug) {
        set({ situacao: 'entrando' });
        try {
          const r = await autenticar('/auth/login', {
            email,
            password: senha,
            ...(tenantSlug ? { tenantSlug } : {}),
          });
          set({
            accessToken: r.accessToken,
            refreshToken: r.refreshToken,
            situacao: r.mfaRequired ? 'mfa-pendente' : 'autenticado',
          });
          if (!r.mfaRequired) await get().carregarContato();
        } catch (erro) {
          set({ situacao: 'anonimo', accessToken: null, refreshToken: null, contato: null });
          throw erro;
        }
      },

      async confirmarMfa(codigo) {
        const r = await autenticar('/auth/mfa', { code: codigo }, get().accessToken);
        set({ accessToken: r.accessToken, refreshToken: r.refreshToken, situacao: 'autenticado' });
        await get().carregarContato();
      },

      async renovar() {
        renovacaoEmCurso ??= (async () => {
          const token = get().refreshToken;
          if (!token) return false;
          try {
            const r = await autenticar('/auth/refresh', { refreshToken: token });
            set({
              accessToken: r.accessToken,
              refreshToken: r.refreshToken,
              situacao: 'autenticado',
            });
            return true;
          } catch {
            set({
              accessToken: null,
              refreshToken: null,
              contato: null,
              situacao: 'anonimo',
            });
            return false;
          } finally {
            renovacaoEmCurso = null;
          }
        })();
        return renovacaoEmCurso;
      },

      /**
       * Quem é o contato, e o que ele pode.
       *
       * As permissões vêm do servidor e são, por construção, só as `portal.*`.
       * A tela as usa para decidir o que desenhar — e nada mais: esconder um
       * botão não é autorização, e cada rota do portal confere a sua.
       */
      async carregarContato() {
        const token = get().accessToken;
        if (!token) return;
        const resposta = await fetch(`${BASE}/auth/me`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (!resposta.ok) return;
        set({ contato: (await resposta.json()) as ContatoDoPortal });
      },

      async sair() {
        const token = get().refreshToken;
        set({ accessToken: null, refreshToken: null, contato: null, situacao: 'anonimo' });
        if (!token) return;
        await fetch(`${BASE}/auth/logout`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refreshToken: token }),
        }).catch(() => {
          // Sair localmente já aconteceu; falha de rede não deve travar a tela
          // nem manter o contato "dentro".
        });
      },
    }),
    {
      name: 'central.portal.auth',
      partialize: (estado) => ({ refreshToken: estado.refreshToken }),
    },
  ),
);

/** `true` quando a sessão do portal carrega a permissão. */
export function podeNoPortal(permissao: string): boolean {
  return usePortalAuth.getState().contato?.permissoes.includes(permissao) ?? false;
}
